import { apiSlice } from "../api-slice";

interface PolicyDocumentParams {
    policyId: number;
    signoff?: boolean;
    watermark?: boolean;
}

export const fileAPI = apiSlice.injectEndpoints({
    endpoints: builder => ({
        viewProposalPDF: builder.query<string, { proposalID: number }>({
            query: ({ proposalID }) => ({
                url: `/api/financial-insurance/template/${proposalID}/view-pdf`,
                responseHandler: (response) => response.blob(),
            }),
            keepUnusedDataFor: 0,
            transformResponse: async (response: Blob) => {
                return URL.createObjectURL(response);
            },
        }),

        downloadProposalPDF: builder.mutation<string, { proposalID: number }>({
            query: ({ proposalID }) => ({
                url: `/api/financial-insurance/template/${proposalID}/download`,
                method: "POST",
                responseHandler: (response) => response.blob(),
            }),
            transformResponse: (response: Blob) => {
                return URL.createObjectURL(response);
            },
        }),

        uploadMasterCSVFile: builder.mutation< any, { proposalID: number; file: File }>({
            query: ({ proposalID, file }) => {
                const formData = new FormData();
                formData.append('file', file);
                return {
                    url: `/api/financial-insurance/${proposalID}/upload-masterfile`,
                    method: 'POST',
                    body: formData,
                };
            },
        }),

        downloadMasterFileCSVFile: builder.mutation<string, { proposalID: number }>({
            query: ({ proposalID }) => ({
                url: `/api/financial-insurance/${proposalID}/download-masterfile`,
                method: "GET",
                responseHandler: (response) => response.blob(),
            }),
            transformResponse: (response: Blob) => {
                return URL.createObjectURL(response);
            },
        }),
        // SUPPORTING DOCS - GMS
        uploadSupportingDocuments: builder.mutation<string, { proposalID: number, files: File[] }>({
            query: ({ proposalID, files }) => {
                const formData = new FormData();
                files.forEach(file => {
                    formData.append("file", file);
                });
                return {
                    url: `/api/financial-insurance/${proposalID}/upload-supporting-details`,
                    method: 'POST',
                    body: formData,
                };
            },
            invalidatesTags: (_result, _error, { proposalID }) => [
                { type: "Proposal", id: proposalID },
                { type: "Proposal", id: "LIST" },
            ],
        }),
        downloadSupportingDocuments: builder.mutation<string, { proposalID: number, file_path: string }>({
            query: ({ proposalID, file_path }) => ({
                url: `/api/financial-insurance/${proposalID}/download-supporting-details?file_path=${file_path}`,
                method: "GET",
                responseHandler: (response) => response.blob(),
            }),
            transformResponse: (response: Blob) => {
                return URL.createObjectURL(response);
            },
        }),
        // UPLOAD SUPPORTING DOCS - ACTUARIAL
        uploadFileDocuments: builder.mutation<string, { proposalID: number, files: File[] }>({
            query: ({ proposalID, files }) => {
                const formData = new FormData();
                files.forEach(file => {
                    formData.append("file", file);
                });
                return {
                    url: `/api/actuarial/${proposalID}/upload-files`,
                    method: 'POST',
                    body: formData,
                };
            },
            invalidatesTags: (_result, _error, { proposalID }) => [
                { type: "Proposal", id: proposalID },
                { type: "Proposal", id: "LIST" },
            ],
        }),
        downloadFileDocuments: builder.mutation<string, { proposalID: number, file_path: string }>({
            query: ({ proposalID, file_path}) => ({
                url: `/api/actuarial/${proposalID}/download-files?file_path=${file_path}`,
                method: "GET",
                responseHandler: (response) => response.blob(),
            }),
            transformResponse: (response: Blob) => {
                return URL.createObjectURL(response);
            },
        }),

        // EBAM: COC File
        viewCOCAsPDF: builder.query<string, { proposalID: number }>({
            query: ({ proposalID }) => ({
                url: `/api/ebam/${proposalID}/coc/view-pdf`,
                responseHandler: (response) => response.blob(),
            }),
            keepUnusedDataFor: 0,
            transformResponse: async (response: Blob) => {
                return URL.createObjectURL(response);
            },
        }),
        downloadCOCAsPDF: builder.mutation<string, { proposalID: number }>({
            query: ({ proposalID }) => ({
                url: `/api/ebam/${proposalID}/coc/download`,
                method: "GET",
                responseHandler: (response) => response.blob(),
            }),
            transformResponse: (response: Blob) => {
                return URL.createObjectURL(response);
            },
        }),
        // downloadCOCAsPDF: builder.mutation<string, { proposalID: number }>({
        //     query: ({ proposalID }) => ({
        //         url: `/api/ebam/${proposalID}/coc/download`,
        //         method: "GET",
        //         responseHandler: async (response) => {
        //             if (!response.ok) {
        //                 const contentType = response.headers.get("content-type") ?? "";

        //                 if (contentType.includes("application/json")) {
        //                     return response.json();
        //                 }
        //                 return response.text();
        //             }
        //             return response.blob();
        //         },
        //     }),
        //     transformResponse: (response: Blob) => {
        //         return URL.createObjectURL(response);
        //     },
        // }),
        // EBAM: POLICY FILE
        viewPolicy: builder.query<string, PolicyDocumentParams>({
            query: ({ policyId, signoff = false, watermark = false }) => ({
                url: `/api/ebam/${policyId}/policy-contract/view-pdf`,
                params: { signoff, watermark, },
                responseHandler: response => response.blob(),
            }),
            keepUnusedDataFor: 0,
            transformResponse: async (response: Blob) => {
                return URL.createObjectURL(response);
            },
        }),
        downloadPolicy: builder.mutation<string, PolicyDocumentParams>({
            query: ({ policyId, signoff = false, watermark = false }) => ({
                url: `/api/ebam/${policyId}/policy-contract/download`,
                method: "GET",
                params: { signoff, watermark, },
                responseHandler: response => response.blob(),
            }),
            transformResponse: (response: Blob) => {
                return URL.createObjectURL(response);
            },
        }),
        // VIEW SELECTED PROTOTYPE TEMPLATE PDF
        viewPrototypeTemplatePFD: builder.mutation<string, { prototypeId: number }>({
            query: ({ prototypeId }) => ({
                url: `api/financial-insurance/prototype-plans/${prototypeId}/view-pdf`,
                method: "GET",
                responseHandler: (response) => response.blob(),
            }),
            transformResponse: (response: Blob) => {
                return URL.createObjectURL(response);
            },
        }),
    }),
});

export const { 
    useLazyViewProposalPDFQuery,
    useDownloadProposalPDFMutation,
    useUploadMasterCSVFileMutation,
    useDownloadMasterFileCSVFileMutation,
    // FOR GMS
    useUploadSupportingDocumentsMutation,
    useDownloadSupportingDocumentsMutation,

    // FOR ACTUARIAL
    useUploadFileDocumentsMutation,
    useDownloadFileDocumentsMutation,

    // COC
    useViewCOCAsPDFQuery,
    useLazyViewCOCAsPDFQuery,
    useDownloadCOCAsPDFMutation,

    // POLICY
    useDownloadPolicyMutation,
    useLazyViewPolicyQuery,
    useViewPolicyQuery,
    // PROTOTYPE
    useViewPrototypeTemplatePFDMutation,
} = fileAPI;